import React from 'react';
import {
  Flex,
  Pill,
  useI18nContext,
  Popover,
  Tooltip,
} from '@procore/core-react';
import PT from 'prop-types';
import LazyLoad from 'react-lazyload';
import styled from 'styled-components';
import { ShieldStar } from '@procore/core-icons';

import { truncate } from '@/react/shared/utils/helpers';
import { SmallLogo } from '@/react/styles/v2';
import SecurityBadge from '@/react/components/SecurtyBadge';
import { renderComponentString } from './ProfessionalCard.utils';
import {
  CardHeader,
  CardBody,
  CardTitle,
  CardTagline,
  CardTypography,
  CardFooter,
  FooterDescription,
  CardLink,
  Card,
  SecurityBadgePill,
} from './ProfessionalCard.styles';

const StyledLazyLoad = styled(LazyLoad)`
  height: 60px;
  width: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const ProfessionalCard = ({ app }) => {
  const i18n = useI18nContext();

  if (!app) return null;

  return (
    <CardLink href={`/apps/${app?.slug}`} variant="gray">
      <Card display="flex" flexDirection="column" gap="lg" padding="lg">
        <CardHeader>
          {(app?.small_logo_url || app?.image_url) && (
            <StyledLazyLoad height={60}>
              <SmallLogo
                style={{
                  borderRadius: '3px',
                }}
                src={
                  app?.small_logo_url ||
                  app?.image_url ||
                  window.imagePath('logo-placeholder.png')
                }
                alt={`${app.name} logo`}
              />
            </StyledLazyLoad>
          )}

          <Flex alignItems="center" gap="sm">
            {app?.status === 'beta' && (
              <Flex alignItems="center" data-testid="app-beta">
                <Pill
                  className="font-size-sm"
                  color="magenta"
                  style={{ borderRadius: '100px' }}
                >
                  {i18n.t('header.beta')}
                </Pill>
              </Flex>
            )}

            {app?.security_questionnaire_visible && (
              <Flex alignItems="center">
                <Popover
                  overlay={<SecurityBadge minimal />}
                  placement="bottom"
                  trigger="hover"
                >
                  <SecurityBadgePill
                    className="font-size-md"
                    color="blue"
                    data-testid="app-security-badge"
                  >
                    <Flex alignItems="center">
                      <ShieldStar size="md" />
                    </Flex>
                  </SecurityBadgePill>
                </Popover>
              </Flex>
            )}
          </Flex>
        </CardHeader>

        <CardBody>
          <CardTitle>{app?.name}</CardTitle>
          <CardTagline>
            {truncate(app?.search_description, 100, true)}
          </CardTagline>
          <CardTypography intent="body">
            {i18n.t('appDetails.labels.byCompany', {
              company: app?.built_by || app?.name,
            })}
          </CardTypography>
          {app?.components && (
            <CardTypography intent="body">
              <Tooltip
                overlay={i18n.t('appTypes.embeddedDescription')}
                placement="bottom"
                trigger="hover"
              >
                <span>{renderComponentString(app.components)}</span>
              </Tooltip>
            </CardTypography>
          )}
        </CardBody>

        <CardFooter>
          <FooterDescription>
            {truncate(app.description || app.search_description, 90, false)}
          </FooterDescription>
        </CardFooter>
      </Card>
    </CardLink>
  );
};

ProfessionalCard.propTypes = {
  app: PT.shape({
    components: PT.arrayOf(PT.string).isRequired,
    image_url: PT.string,
    small_logo_url: PT.string,
    slug: PT.string.isRequired,
    name: PT.string.isRequired,
    search_description: PT.string.isRequired,
    description: PT.string,
    built_by: PT.string,
    status: PT.string.isRequired,
    security_questionnaire_visible: PT.bool,
  }).isRequired,
};
